import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Avatar,
  Skeleton,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Autorenew as AutorenewIcon,
  WarningAmber as WarningAmberIcon,
  VpnKey as KeyIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { CopyRow, CopyIconButton } from './SamlConnectionDetails';
import {
  ConnectionSectionHeading,
  accentFor,
  panelPaperSx,
} from './ConnectionPanelSection';
import { ReadOnlyButton } from '../common/ReadOnlyButton';
import apiService from '../../services/api';

// OpenID Connect accent - Teal. Distinct from the SAML violet so the two protocol
// panels are not mistaken for each other at a glance. Both accents live in
// ConnectionPanelSection so neither panel can quietly restyle itself away from the other.
const { main: accentColor, light: accentColorLight } = accentFor('openid-connect');

/*
 * Wide enough to read as "there is a long value behind this" without implying a length.
 * Keycloak 26.7.0 lengthened client secrets to 86 characters, so nothing here may assume
 * the old 32: the revealed value is rendered full-width and wraps rather than truncating.
 */
const SECRET_MASK = '••••••••••••••••••••••••';

/** The secret lives in exactly one place: `data.clientSecret` on the secret routes' envelope. */
const readSecret = (response) => response?.data?.clientSecret || response?.clientSecret || '';

const cardSx = {
  backgroundColor: '#FFFFFF',
  border: '1px solid #E5E5E5',
  borderRadius: '6px',
  p: 2,
  mb: 1.5,
};

const labelSx = {
  color: '#999999',
  fontFamily: 'Metropolis, sans-serif',
  fontWeight: 500,
};

const hintSx = {
  color: '#666666',
  fontFamily: 'Metropolis, sans-serif',
  display: 'block',
  mt: 0.75,
};

/**
 * The per-realm endpoint set, in the order an admin needs it.
 *
 * Discovery first and on its own: an OIDC library that supports discovery needs this one
 * value and derives the rest, so burying it in an alphabetical list costs support tickets.
 */
const realmRows = (endpoints) => [
  {
    key: 'discoveryUrl',
    label: 'Discovery URL (.well-known/openid-configuration)',
    value: endpoints.discoveryUrl,
    hint:
      'Start here. Most OIDC libraries need only this one value — they read every endpoint ' +
      'below out of it. Public and unauthenticated.',
    external: true,
  },
  {
    key: 'issuer',
    label: 'Issuer',
    value: endpoints.issuer,
    hint: 'The iss claim on every token Prism issues. Validate it exactly, including the scheme.',
  },
  {
    key: 'authorizationUrl',
    label: 'Authorization endpoint',
    value: endpoints.authorizationUrl,
    hint: 'Where you send the browser to start a sign-in.',
  },
  {
    key: 'tokenUrl',
    label: 'Token endpoint',
    value: endpoints.tokenUrl,
    hint: 'Back-channel exchange of the authorization code for tokens.',
  },
  {
    key: 'userInfoUrl',
    label: 'UserInfo endpoint',
    value: endpoints.userInfoUrl,
    hint: 'Claims for the signed-in user, presented with the access token.',
  },
  {
    key: 'jwksUrl',
    label: 'JWKS URL',
    value: endpoints.jwksUrl,
    hint: 'Signing keys for token validation. Cache them, and re-fetch on an unknown kid.',
  },
  {
    key: 'endSessionUrl',
    label: 'End session endpoint',
    value: endpoints.endSessionUrl,
    hint: 'RP-initiated logout. Signing out of the application alone leaves the Prism session live.',
  },
];

/**
 * One realm row while its value is still in flight.
 *
 * A skeleton rather than CopyRow's '--' placeholder: '--' is how this panel says "there is
 * no value", and painting it for half a second before the real URL arrives reads as a
 * broken tenant. The label and hint are kept so the section does not reflow when the
 * values land, and no copy control is offered for a value that does not exist yet.
 */
const EndpointSkeletonRow = ({ label, hint }) => (
  <Box sx={cardSx} data-testid="endpoint-skeleton">
    <Typography variant="caption" sx={labelSx}>
      {label}
    </Typography>
    <Skeleton variant="text" width="70%" sx={{ mt: 0.5 }} />
    {hint && (
      <Typography variant="caption" sx={hintSx}>
        {hint}
      </Typography>
    )}
  </Box>
);

/**
 * The OIDC half of the application detail view's connection panel.
 *
 * Structured around the distinction that actually matters when an admin is configuring the
 * far side: the realm values are identical for every application in the tenant and can be
 * pasted from any of them, while the client values below are unique to this application.
 *
 * There is deliberately no metadata-download control. SAML's descriptor XML has no OIDC
 * counterpart; the discovery document is what replaces it, and it is a live URL, not a file.
 *
 * Copy state is owned by the parent (`copiedKey` + `onCopy(key, value)`) so that the SAML and
 * OIDC panels share one tick-at-a-time behaviour — see CopyRow's contract.
 *
 * The realm endpoint set is a PROP, not read off `app`. The list response nulls it — it is
 * identical for every application in the tenant, so the backend sends it only on the single
 * GET — and this panel is rendered from list state, so the owner re-reads the single record
 * and hands the result down along with its in-flight and failed states. Falling back to
 * `app.oidcEndpoints` keeps a caller that already holds a full record working unchanged.
 *
 * @param {object} app        - a CustomApplicationResponse with protocol 'openid-connect'
 * @param {string} customerId - tenant id, for the secret routes
 * @param {string} copiedKey  - the parent's most-recently-copied row key
 * @param {Function} onCopy   - the parent's handleCopy(key, value)
 * @param {object} endpoints  - the realm endpoint set, once the owner has it
 * @param {boolean} endpointsLoading - true while the owner is fetching it
 * @param {string} endpointsError    - why the fetch failed, or null
 */
const OidcConnectionDetails = ({
  app,
  customerId,
  copiedKey,
  onCopy,
  endpoints,
  endpointsLoading = false,
  endpointsError = null,
}) => {
  /*
   * The revealed secret. Component state and nothing else: never localStorage, never
   * sessionStorage, never a module-level cache, never logged. Unmounting the panel drops
   * it, which is the re-mask-on-close requirement.
   *
   * Every route in and out of this panel unmounts it, including an in-place swap of `app`:
   * the owner renders it with key={app.id}. Re-masking in an effect instead would run
   * after paint, which is one frame of the previous application's secret under the new
   * application's heading — and would leave the regenerate dialog's state alive across it.
   */
  const [secret, setSecret] = useState('');
  const [revealing, setRevealing] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  const realmEndpoints = endpoints || app.oidcEndpoints || {};
  const oidc = app.oidc || {};
  const redirectUris = oidc.redirectUris || [];

  /*
   * A missing `oidc` object means UNKNOWN, never EMPTY.
   *
   * When Keycloak does not answer, the list handler deliberately still returns the row from
   * Prism's own database — with `oidc` nil and `hasSecret` false — so the application does not
   * vanish from the admin's list. Read field-by-field that is indistinguishable from a
   * correctly-configured client that happens to have no redirect URIs and no secret, which is
   * how this panel came to tell admins "None configured — sign-in will fail" about tenants
   * whose sign-in was working perfectly, and to remove the Reveal and Regenerate controls that
   * are the only way to recover a credential.
   *
   * Every OIDC client Prism can create has at least one redirect URI (the create validator
   * requires it), so an OIDC application reaching this panel with no `oidc` object at all is
   * the degraded response and nothing else.
   */
  const configUnavailable = !app.oidc;

  // A public client has no secret at all, so there is nothing to reveal or rotate.
  // hasSecret is omitted rather than sent false for those, so neither signal alone is
  // sufficient: publicClient decides when present, hasSecret decides otherwise.
  const isPublicClient = oidc.publicClient === true;
  const hasSecret = !isPublicClient && (app.hasSecret === true || oidc.publicClient === false);

  const handleReveal = async () => {
    setRevealing(true);
    try {
      const response = await apiService.getCustomApplicationSecret(customerId, app.id);
      if (response?.success === false) {
        toast.error(response.error || 'Failed to fetch the client secret');
        return;
      }
      const value = readSecret(response);
      if (!value) {
        toast.error('The server did not return a client secret');
        return;
      }
      setSecret(value);
    } catch (error) {
      // Deliberately not console.error'd, unlike every other handler in this view: the
      // response body on these two routes is a live credential, and a thrown error can
      // carry it. Only the message reaches the admin, and never the browser console.
      toast.error(error.message || 'Failed to fetch the client secret');
    } finally {
      setRevealing(false);
    }
  };

  const closeRegenerateDialog = () => {
    setShowRegenerateDialog(false);
    setAcknowledged(false);
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const response = await apiService.regenerateCustomApplicationSecret(customerId, app.id);
      if (response?.success === false) {
        toast.error(response.error || 'Failed to regenerate the client secret');
        return;
      }
      // The rotation has already happened server-side by now, so the dialog closes either
      // way: leaving it open on a malformed response invites a second, pointless rotation.
      closeRegenerateDialog();

      const value = readSecret(response);
      if (!value) {
        // Whatever is on screen is the secret that was just invalidated, so drop it rather
        // than leave a dead value looking live. Only the free look at the new one is lost.
        setSecret('');
        toast.error(
          'Client secret regenerated, but the server did not return the new value — use Reveal to read it'
        );
        return;
      }
      // Shown straight away: the admin cannot deploy what they cannot see, and they are
      // already mid-outage at this point.
      setSecret(value);
      toast.success('Client secret regenerated — deploy the new value now');
    } catch (error) {
      // Same no-logging rule as handleReveal.
      toast.error(error.message || 'Failed to regenerate the client secret');
    } finally {
      setRegenerating(false);
    }
  };

  const revealed = Boolean(secret);

  return (
    <>
      <Paper elevation={0} sx={panelPaperSx}>
        {/* ---- Per-client: unique to this application ---- */}
        <ConnectionSectionHeading
          title="This application"
          chipLabel="OpenID Connect"
          accent={accentColor}
          accentLight={accentColorLight}
          hint="Unique to this application. Nothing else in your tenant shares these values."
        />

        {configUnavailable && (
          <Alert
            severity="warning"
            data-testid="oidc-config-degraded"
            sx={{
              mb: 1.5,
              borderRadius: '6px',
              '& .MuiAlert-message': { fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem' },
            }}
          >
            Configuration could not be read from the identity server, so this application's
            redirect URIs and client secret cannot be shown. This is not a statement about the
            application — sign-in is unaffected, and nothing has been changed or lost. Reload the
            page to try again.
          </Alert>
        )}

        <CopyRow
          label="Client ID"
          hint="The client_id your application sends on every authorization and token request."
          value={app.clientId}
          copied={copiedKey === 'clientId'}
          onCopy={() => onCopy('clientId', app.clientId)}
        />

        {isPublicClient ? (
          <Box sx={cardSx}>
            <Typography variant="caption" sx={labelSx}>
              Client secret
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: '#253E66', fontFamily: 'Metropolis, sans-serif', mt: 0.5 }}
            >
              None — this is a public client.
            </Typography>
            <Typography variant="caption" sx={hintSx}>
              Public clients cannot hold a secret. Authorization Code with PKCE (S256) is used
              instead, and the client type cannot be changed after creation.
            </Typography>
          </Box>
        ) : hasSecret || configUnavailable ? (
          /*
            Kept on screen in the degraded case rather than dropped. The controls are the only
            route to a credential the admin may be mid-incident over; removing the card says
            "this client has no secret", which is a claim the degraded response cannot support.
          */
          <Box sx={cardSx}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" sx={labelSx}>
                  Client secret
                </Typography>
                {/*
                  Full width and break-all: a real Keycloak 26.7.0 secret is 86 characters and
                  must wrap onto a second line rather than be cut off at the card edge.
                */}
                <Typography
                  variant="body2"
                  data-testid="client-secret-value"
                  sx={{
                    color: '#253E66',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    wordBreak: 'break-all',
                    mt: 0.5,
                  }}
                >
                  {revealed ? secret : SECRET_MASK}
                </Typography>
                <Typography variant="caption" sx={hintSx}>
                  {configUnavailable
                    ? 'Unavailable while the identity server cannot be reached — Prism never stores this value, it re-reads it, so there is nothing to show until the server answers. Reveal and Regenerate are disabled until then.'
                    : revealed
                    ? 'Visible until you hide it or leave this page. Prism does not store it.'
                    : 'Hidden. Prism never stores this value — revealing it re-reads it from the identity server.'}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                {revealed && (
                  <CopyIconButton
                    label="client secret"
                    value={secret}
                    copied={copiedKey === 'clientSecret'}
                    onCopy={() => onCopy('clientSecret', secret)}
                    sx={{
                      color: copiedKey === 'clientSecret' ? '#27AE60' : accentColor,
                      backgroundColor: copiedKey === 'clientSecret' ? '#E8F8F0' : accentColorLight,
                      border: '1px solid #E5E5E5',
                      borderRadius: '6px',
                    }}
                  />
                )}
                {/*
                  ReadOnlyButton, exactly like Regenerate beside it: the backend requires
                  sso_admin for BOTH secret routes, so a read-only admin clicking Reveal
                  gets a 403 and an error toast instead of a control that was never theirs.
                  Hide is the same control in its revealed state, which a read-only admin
                  can never reach.
                */}
                <ReadOnlyButton
                  size="small"
                  variant="outlined"
                  onClick={revealed ? () => setSecret('') : handleReveal}
                  disabled={revealing || configUnavailable}
                  startIcon={
                    revealing ? (
                      <CircularProgress size={14} />
                    ) : revealed ? (
                      <VisibilityOffIcon sx={{ fontSize: 16 }} />
                    ) : (
                      <VisibilityIcon sx={{ fontSize: 16 }} />
                    )
                  }
                  sx={{
                    color: accentColor,
                    borderColor: '#E5E5E5',
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '6px',
                    whiteSpace: 'nowrap',
                    '&:hover': { borderColor: accentColor, backgroundColor: accentColorLight },
                  }}
                >
                  {revealed ? 'Hide' : 'Reveal'}
                </ReadOnlyButton>
                <ReadOnlyButton
                  size="small"
                  variant="outlined"
                  onClick={() => setShowRegenerateDialog(true)}
                  disabled={configUnavailable}
                  startIcon={<AutorenewIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    color: '#E74C3C',
                    borderColor: '#E74C3C40',
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '6px',
                    whiteSpace: 'nowrap',
                    '&:hover': { borderColor: '#E74C3C', backgroundColor: '#FDF2F2' },
                  }}
                >
                  Regenerate
                </ReadOnlyButton>
              </Box>
            </Box>
          </Box>
        ) : null}

        {/*
          The empty-state assertion below is only made when the list is KNOWN to be empty. In
          the degraded case there is no list to report on at all, and the warning above already
          says why, so nothing is rendered here rather than a confident claim about a
          configuration that was never read.
        */}
        {configUnavailable ? null : redirectUris.length === 0 ? (
          <Box sx={cardSx}>
            <Typography variant="caption" sx={labelSx}>
              Redirect URIs
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: '#253E66', fontFamily: 'Metropolis, sans-serif', mt: 0.5 }}
            >
              None configured — sign-in will fail until at least one is added.
            </Typography>
          </Box>
        ) : (
          redirectUris.map((uri, index) => (
            <CopyRow
              key={`${uri}-${index}`}
              label={redirectUris.length > 1 ? `Redirect URI ${index + 1}` : 'Redirect URI'}
              hint={
                index === 0
                  ? 'Prism matches redirect_uri against this list literally. A missing entry fails the request before the user sees a login page.'
                  : undefined
              }
              value={uri}
              copied={copiedKey === `redirectUri:${index}`}
              onCopy={() => onCopy(`redirectUri:${index}`, uri)}
            />
          ))
        )}

        {/* ---- Per-realm: shared by every application in the tenant ---- */}
        <Box sx={{ mt: 3 }}>
          <ConnectionSectionHeading
            title="Realm endpoints"
            accent={accentColor}
            accentLight={accentColorLight}
            hint="Identical for every application in your tenant — these describe Prism, not this application."
          />

          {endpointsError && !endpointsLoading && (
            <Alert
              severity="warning"
              sx={{
                mb: 1.5,
                borderRadius: '6px',
                '& .MuiAlert-message': { fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem' },
              }}
            >
              The realm endpoints could not be loaded — reload the page to try again. Everything
              above is unaffected. ({endpointsError})
            </Alert>
          )}

          {endpointsLoading
            ? realmRows({}).map((row) => (
                <EndpointSkeletonRow key={row.key} label={row.label} hint={row.hint} />
              ))
            : realmRows(realmEndpoints).map((row) => (
                <CopyRow
                  key={row.key}
                  label={row.label}
                  hint={row.hint}
                  value={row.value}
                  copied={copiedKey === row.key}
                  onCopy={() => onCopy(row.key, row.value)}
                  external={row.external}
                />
              ))}
        </Box>
      </Paper>

      {/*
        Regenerate confirmation. A house-style MUI dialog rather than window.confirm, and
        gated on an explicit acknowledgement rather than a single click, because the action
        it guards takes the customer's application down with no way back.
      */}
      <Dialog
        open={showRegenerateDialog}
        onClose={regenerating ? undefined : closeRegenerateDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', border: '1px solid #E5E5E5', backgroundColor: '#FFFFFF' } }}
      >
        <DialogTitle
          sx={{
            backgroundColor: '#FDF2F2',
            borderBottom: '1px solid #E5E5E5',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            py: 2,
            px: 3,
          }}
        >
          <Avatar sx={{ backgroundColor: '#E74C3C', width: 40, height: 40 }}>
            <WarningAmberIcon />
          </Avatar>
          <Typography
            sx={{ fontFamily: 'Bitter, Georgia, serif', fontWeight: 600, fontSize: '1.25rem', color: '#253E66' }}
          >
            Regenerate client secret
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ py: 3, px: 3 }}>
          <Alert
            severity="error"
            icon={<KeyIcon />}
            sx={{
              mb: 2,
              backgroundColor: '#FDF2F2',
              border: '1px solid #E74C3C20',
              borderRadius: '6px',
              '& .MuiAlert-icon': { color: '#E74C3C' },
              '& .MuiAlert-message': { fontFamily: 'Metropolis, sans-serif', color: '#253E66' },
            }}
          >
            This is a hard replace with no grace period. The old secret stops working the instant
            you confirm — there is no dual-secret rotation window and no way to undo it.
          </Alert>

          <Typography
            sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.95rem', color: '#253E66', lineHeight: 1.6 }}
          >
            {app.name} will fail to authenticate from that moment and stays broken until the new
            secret is deployed everywhere it is used — every environment, every running instance.
            Anything that signs in through it is down in between.
          </Typography>
          <Typography
            sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#666666', mt: 1.5 }}
          >
            The new secret is shown here as soon as it is issued, and can be revealed again at any
            time.
          </Typography>

          <FormControlLabel
            sx={{ mt: 2, alignItems: 'flex-start' }}
            control={
              <Checkbox
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                sx={{ color: '#E5E5E5', '&.Mui-checked': { color: '#E74C3C' }, pt: 0.5 }}
              />
            }
            label={
              <Typography
                sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', color: '#253E66' }}
              >
                I understand {app.name} will stop working immediately and will stay broken until I
                deploy the new secret.
              </Typography>
            }
          />
        </DialogContent>

        <DialogActions
          sx={{ px: 3, py: 2, gap: 1.5, borderTop: '1px solid #E5E5E5', backgroundColor: '#F9FBFD' }}
        >
          <Button
            onClick={closeRegenerateDialog}
            disabled={regenerating}
            variant="outlined"
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '6px',
              borderColor: '#E5E5E5',
              color: '#666666',
              px: 3,
            }}
          >
            Cancel
          </Button>
          <ReadOnlyButton
            onClick={handleRegenerate}
            disabled={!acknowledged || regenerating}
            variant="contained"
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '6px',
              px: 3,
              backgroundColor: '#E74C3C',
              '&:hover': { backgroundColor: '#C0392B' },
            }}
          >
            {regenerating ? 'Regenerating...' : 'Regenerate secret'}
          </ReadOnlyButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OidcConnectionDetails;
